import { PencilIcon } from '@heroicons/react/24/outline';
import React from 'react';

import {
  DiscriminatedTreatmentRequestUpdatedEvent,
  TreatmentRecordRequestSchema,
} from '@/services/api/generated/matterChangesService';

import {
  ActivityActions,
  ActivityBody,
  ActivityDescription,
  ActivityIcon,
  ActivityItem,
  ActivityLink,
  CollapsibleList,
  FieldsChanged,
} from '../activity-feed-v2';
import { getUserFromEvent } from '../activity-feed-v2/ActivityDiffList';
import { formatEnumValue, formatFieldName } from '../activity-feed-v2/utils';

export interface TreatmentRequestUpdatedEventV2Props {
  event: DiscriminatedTreatmentRequestUpdatedEvent;
}

/**
 * V2 renderer for Treatment Request Updated events that uses the `previous` field
 * to show only what changed, instead of parsing the diff format.
 */
export const TreatmentRequestUpdatedEventV2: React.FC<
  TreatmentRequestUpdatedEventV2Props
> = ({ event }) => {
  const { treatment_request, previous } = event.payload;
  const user = getUserFromEvent(event);

  const icon = (
    <ActivityIcon
      icon={
        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-purple-100">
          <PencilIcon className="h-4 w-4 text-purple-600" />
        </div>
      }
    />
  );

  // Custom field renderer for treatment request-specific fields
  const renderField = (
    name: keyof TreatmentRecordRequestSchema,
    value: TreatmentRecordRequestSchema[keyof TreatmentRecordRequestSchema],
  ) => {
    const label = formatFieldName(String(name));

    // Provided records array
    if (name === 'provided_records' && Array.isArray(value)) {
      const records = value;
      if (records.length === 0) return null;

      if (records.length <= 3) {
        return (
          <ActivityDescription>
            <span className="font-medium">{label}:</span> {records.join(', ')}
          </ActivityDescription>
        );
      }

      return (
        <div>
          <ActivityDescription>
            <span className="font-medium">{label}:</span>
          </ActivityDescription>
          <CollapsibleList
            items={records.map((record, idx) => (
              <span key={idx}>{record}</span>
            ))}
            showLabel={`Show Records (${records.length})`}
            hideLabel="Hide Records"
          />
        </div>
      );
    }

    // Channel object - show details
    if (name === 'channel' && value && typeof value === 'object') {
      const channel = value as Record<string, unknown>;
      const details: string[] = [];

      if (channel.type)
        details.push(`Type: ${formatEnumValue(String(channel.type))}`);
      if (channel.phone_number)
        details.push(`Phone: ${String(channel.phone_number)}`);
      if (channel.email) details.push(`Email: ${String(channel.email)}`);
      if (channel.fax) details.push(`Fax: ${String(channel.fax)}`);
      if (channel.address) details.push(`Address: ${String(channel.address)}`);
      if (channel.instructions)
        details.push(`Instructions: ${String(channel.instructions)}`);

      if (details.length === 0) return null;

      return (
        <div>
          <ActivityDescription>
            <span className="font-medium">{label}:</span>
          </ActivityDescription>
          <CollapsibleList
            items={details.map((detail, idx) => (
              <span key={idx}>{detail}</span>
            ))}
            showLabel={`Show Channel Details (${details.length})`}
            hideLabel="Hide Channel Details"
            defaultExpanded={details.length <= 3}
          />
        </div>
      );
    }

    // Fax transmission status
    if (
      name === 'fax_transmission_status' &&
      value &&
      typeof value === 'object'
    ) {
      const faxStatus = value as Record<string, unknown>;
      const details: string[] = [];

      if (faxStatus.status)
        details.push(`Status: ${formatEnumValue(String(faxStatus.status))}`);
      if (faxStatus.transmitted_at)
        details.push(
          `Transmitted: ${new Date(String(faxStatus.transmitted_at)).toLocaleString()}`,
        );
      if (faxStatus.error_message)
        details.push(`Error: ${String(faxStatus.error_message)}`);

      if (details.length === 0) return null;

      return (
        <div>
          <ActivityDescription>
            <span className="font-medium">{label}:</span>
          </ActivityDescription>
          <CollapsibleList
            items={details.map((detail, idx) => (
              <span key={idx}>{detail}</span>
            ))}
            showLabel="Show Fax Status"
            hideLabel="Hide Fax Status"
            defaultExpanded
          />
        </div>
      );
    }

    // Metadata object - show key automation fields
    if (name === 'metadata' && value && typeof value === 'object') {
      const metadata = value as Record<string, unknown>;
      const details: string[] = [];

      if (metadata.disable_automations !== undefined)
        details.push(
          `Automations: ${metadata.disable_automations ? 'Disabled' : 'Enabled'}`,
        );
      if (
        metadata.automation_state_v2 &&
        typeof metadata.automation_state_v2 === 'object'
      ) {
        const state = metadata.automation_state_v2 as Record<string, unknown>;
        if (state.status)
          details.push(
            `Automation Status: ${formatEnumValue(String(state.status))}`,
          );
      }

      if (details.length === 0) return null;

      return (
        <div>
          <ActivityDescription>
            <span className="font-medium">{label}:</span>
          </ActivityDescription>
          <CollapsibleList
            items={details.map((detail, idx) => (
              <span key={idx}>{detail}</span>
            ))}
            showLabel="Show Metadata"
            hideLabel="Hide Metadata"
            defaultExpanded
          />
        </div>
      );
    }

    // Boolean fields
    if (
      (name === 'is_certified' || name === 'is_urgent') &&
      typeof value === 'boolean'
    ) {
      return (
        <ActivityDescription>
          <span className="font-medium">{label}:</span> {value ? 'Yes' : 'No'}
        </ActivityDescription>
      );
    }

    // Enum fields
    if (name === 'status' || name === 'actor') {
      return (
        <ActivityDescription>
          <span className="font-medium">{label}:</span>{' '}
          {formatEnumValue(String(value))}
        </ActivityDescription>
      );
    }

    // Default rendering for simple fields
    return (
      <ActivityDescription>
        <span className="font-medium">{label}:</span> {String(value)}
      </ActivityDescription>
    );
  };

  const body = (
    <ActivityBody>
      <FieldsChanged<TreatmentRecordRequestSchema>
        previous={previous}
        current={treatment_request}
        fieldsToCheck={[
          'actor',
          'is_certified',
          'is_urgent',
          'provided_records',
          'status',
          'metadata',
          'channel',
          'fax_transmission_status',
        ]}
        renderField={renderField}
      />
    </ActivityBody>
  );

  const actions = event.payload.treatment_request?.provider_id && (
    <ActivityActions>
      <ActivityLink
        href={`/matters/${event.matter_id}/treatment/${event.payload.treatment_request.provider_id}`}
      >
        View Provider
      </ActivityLink>
    </ActivityActions>
  );

  return (
    <ActivityItem
      icon={icon}
      title="Treatment Request Updated"
      timestamp={new Date(event.occurred_at ?? new Date())}
      user={user}
      body={body}
      actions={actions}
    />
  );
};
